import { DownloadAppBanner } from "components/Banner/DownloadAppBanner";
import { IconDashboard } from "components/Icon/IconDashboard";
import { IconGaiaMenu } from "components/Icon/IconGaiaMenu";
import { IconHome } from "components/Icon/IconHome";
import { IconLeaderboard } from "components/Icon/IconLeaderboard";
import { IconMyClass } from "components/Icon/IconMyClass";
import { IconPlayCircle } from "components/Icon/IconPlayCircle";
import { IconSearch } from "components/Icon/IconSearch";
import { useAvatarStore } from "components/InteractiveAvatar/avatarStore";
import { Logo } from "components/Logo";
import { useSystemStore } from "components/PageHeader/systemStore";
import { SkeletonRect, SkeletonWrapper } from "components/Skeleton/Skeleton";
import { trackEvent, trackEventGA, trackEventGTM } from "lib/amplitude";
import { CONST } from "lib/const";
import { getCookie } from "lib/helper";
import {
  getSubDomain,
  isBlueBirdURL,
  isSubdomainBluebird,
  useIsSubdomain,
  useIsTenant,
} from "lib/host";
import { ReduceArrayFromDuplicate } from "lib/object";
import { isBrowser } from "lib/ssr";
import { useAuthStore } from "modules/Auth/authStore";
import {
  useAccessGaiaTenant,
  useGetSubdomainData,
} from "modules/Tenant/homeTenantApi";
import useTranslation from "next-translate/useTranslation";
import Image from "next/image";
import { useRouter } from "next/router";
import React from "react";
import { useEffect, useState } from "react";

export const iconColor = {
  default: "#817E8C",
  selected: "#19093A",
};

const labelColor = {
  default: "text-neutral-50 text-c1 font-light text-center",
  selected: "text-purple-80 text-c1 font-bold text-center",
};

interface MenuProps {
  id?: string;
  href?: string;
  label?: string;
}

export const BottomNav: React.FC<{
  isDesktop?: boolean;
  isVisible?: boolean;
  className?: string;
}> = ({ isDesktop, isVisible, className }) => {
  const subDomain = isBrowser() ? getSubDomain(window.location.hostname) : null;
  const loginState = useAuthStore((state) => state.loginState);
  const router = useRouter();
  const isTenant = useIsTenant();
  const isSubdomainTenant = useIsSubdomain();
  const currentUser = useAuthStore((state) => state.currentUser);
  const [isLoading, setIsLoading] = useState(true);
  const theme = useSystemStore((state) => state.theme);
  const isBlueBirdTenant =
    typeof window !== "undefined" &&
    (isBlueBirdURL(window.location.hostname) ||
      isSubdomainBluebird(window.location.hostname));

  const referralCode =
    getCookie("ref_code_gk") && getCookie("ref_code_gk") !== "null"
      ? getCookie("ref_code_gk")
      : typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("referral")
      : "";

  const [menus, setMenus] = useState([
    {
      id: "my-home",
      href: "/",
      label: "Home",
    },
    {
      id: "my-account",
      href: loginState == "LOGIN" ? "/account" : "/login?url=account",
      label: "Akun Saya",
    },
  ]);

  const trackNavbar = (type: string) => {
    trackEvent({
      event: "nav_a",
      property: {
        button_type: type,
        page: router.pathname,
      },
    });

    trackEventGTM({
      event: "nav_a",
      property: {
        button_type: type,
        page: router.pathname,
      },
    });

    trackEventGA({
      event: "nav_a",
      property: {
        button_type: type,
        page: router.pathname,
      },
    });
  };

  const gaiaAccess = useAccessGaiaTenant();
  const [isGaiaAccess, setIsGaiaAccess] = useState(true);
  const [isGaiaLoading, setIsGaiaLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      setIsGaiaLoading(true);
      gaiaAccess.doFetch(
        { slug: currentUser?.referral ?? subDomain },
        {
          onSuccess(data: any) {
            setIsGaiaAccess(checkGaiaAccess(data?.data, currentUser));
            setIsGaiaLoading(false);
          },
          onError() {
            setIsGaiaLoading(false);
          },
        }
      );
    }
  }, [currentUser]);

  function checkGaiaAccess(accessData, user) {
    const groupedAccess = {
      positions: [],
      workUnits: [],
      locations: [],
    };

    let isUserAccess = false;

    accessData.forEach((access) => {
      const accessType = access.access_type.name;
      const isExactUser = access.is_exact_user;
      const isOpenToEveryone = access.is_open_to_everyone;

      if (accessType === "User" && (isExactUser || isOpenToEveryone)) {
        isUserAccess = true;
      }

      const typeData = access.type_json_data || {};

      if (typeData.positions) {
        groupedAccess.positions.push(...typeData.positions);
      }

      if (typeData.work_units) {
        groupedAccess.workUnits.push(...typeData.work_units);
      }

      if (typeData.locations) {
        groupedAccess.locations.push(...typeData.locations);
      }
    });

    const allowedPositions = groupedAccess.positions
      .map((p) => p?.position)
      .filter(Boolean);
    const allowedWorkUnits = groupedAccess.workUnits
      .map((wu) => wu?.work_unit)
      .filter(Boolean);
    const allowedLocations = groupedAccess.locations
      .map((loc) => loc?.location)
      .filter(Boolean);

    const hasPositionAccess = allowedPositions.includes(
      user?.profile?.position
    );
    const hasWorkUnitAccess = allowedWorkUnits.includes(
      user?.profile?.work_unit
    );
    const hasLocationAccess = allowedLocations.includes(
      user?.profile?.location
    );

    return (
      isUserAccess ||
      hasPositionAccess ||
      hasWorkUnitAccess ||
      hasLocationAccess
    );
  }

  // Allowing list of client to show leaderboard
  const clientLeaderboards = [
    "kemenperin",
    "demoplus",
    "umkm",
    "desa",
    "wonderfullearning",
    "kapalapi",
    "ppsdmaparatur",
    "kemkes",
    // "bluebird",
  ];

  const isB2B = useIsSubdomain() || useIsTenant();

  const [isGaiaEnabled, setIsGaiaEnabled] = useState(false);
  // @ts-ignore
  const getSubdomainAPI = useGetSubdomainData();
  const { tenantData, setTenantData } = useAuthStore();

  useEffect(() => {
    getSubdomainAPI.doFetch(
      {
        slug:
          window.location.hostname === "e-learning.bluebirdgroup.com"
            ? "bluebird"
            : window.location.hostname === "lms.injourney.id"
            ? "injourney"
            : currentUser?.referral ?? subDomain,
      },
      {
        onSuccess(data: any) {
          setTenantData(data?.data);
        },
      }
    );
  }, [currentUser, subDomain]);

  useEffect(() => {
    if (tenantData) {
      setIsGaiaEnabled((tenantData as any).setting.is_heygen_enabled);
    }
  }, [tenantData]);

  const [isLeaderboard, setIsLeaderboard] = useState(false);
  const [isPIC, setIsPIC] = useState(false);

  useEffect(() => {
    if (
      currentUser &&
      currentUser?.dashboard_url &&
      menus.length &&
      !menus.some((a) => a.id == "dashboard")
    ) {
      setIsPIC(true);
    }
  }, [currentUser]);

  useEffect(() => {
    if (
      currentUser?.tenant?.status === "Trial" ||
      clientLeaderboards.includes(currentUser?.referral) ||
      CONST.IS_DEV ||
      (isB2B && currentUser?.referral !== "bluebird")
    ) {
      setIsLeaderboard(true);
    }
  }, [currentUser, isB2B]);

  const { t } = useTranslation("common");

  useEffect(() => {
    if (isGaiaLoading) return;

    let newMenus = [
      {
        id: "my-home",
        href: "/",
        label: "Home",
      },
    ];

    // if (currentUser && !isPIC && isLeaderboard) {
    //   newMenus.push({
    //     id: "my-class",
    //     href: "/my-class",
    //     label: "Kelas Saya",
    //   });
    // }

    if (currentUser && (isPIC || (!isPIC && !isGaiaAccess && !isGaiaEnabled))) {
      newMenus.push({
        id: "my-class",
        href: "/my-class",
        label: "Kelas Saya",
      });
    }

    if (currentUser && isGaiaAccess && isGaiaEnabled) {
      newMenus.push({
        id: "gaia",
        href: "?openGaia=true",
        label: "GAIA",
      });
    }

    // if (currentUser && isLeaderboard) {
    //   newMenus.push({
    //     id: "leaderboard",
    //     href: "/leaderboard",
    //     label: "Leaderboard",
    //   });
    // }

    if (currentUser && isPIC) {
      newMenus.push({
        id: "dashboard",
        href: "/dashboard",
        label: "Dashboard",
      });
    }
    newMenus.push({
      id: "my-account",
      href: loginState == "LOGIN" ? "/account" : "/login?url=account",
      label: "Akun Saya",
    });

    if (newMenus.length > 5) {
      if (isPIC) {
        newMenus = [
          newMenus[0],
          newMenus.find((m) => m.id === "leaderboard") || newMenus[1],
          newMenus[1],
          newMenus.find((m) => m.id === "dashboard") || newMenus[2],
          newMenus[newMenus.length - 1],
        ];
      } else {
        newMenus = [
          newMenus[0],
          newMenus.find((m) => m.id === "my-class") || newMenus[1],
          newMenus[1],
          newMenus.find((m) => m.id === "leaderboard") || newMenus[2],
          newMenus[newMenus.length - 1],
        ];
      }
    }

    if (newMenus.length % 2 === 1) {
      const middleIndex = Math.floor(newMenus.length / 2);
      const gaiaIndex = newMenus.findIndex((m) => m.id === "gaia");
      if (gaiaIndex !== -1 && gaiaIndex !== middleIndex) {
        const [gaia] = newMenus.splice(gaiaIndex, 1);
        newMenus.splice(middleIndex, 0, gaia);
      }
    }

    setMenus(newMenus);
  }, [
    isGaiaLoading,
    currentUser,
    isSubdomainTenant,
    isTenant,
    isGaiaAccess,
    isPIC,
    isLeaderboard,
  ]);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  const renderUser = (bgColor: string, textColor: string) => {
    const initialName = currentUser?.name
      ? currentUser?.name[0].toUpperCase() ?? "U"
      : currentUser?.email
      ? currentUser?.email[0].toUpperCase() ?? "U"
      : "U";

    return (
      <div
        className={`bg-[${bgColor}] cursor-pointer w-24 h-24 rounded-full flex items-center justify-center text-b1 text-violet_main`}
      >
        {initialName}
      </div>
    );
  };

  const { isLoadingSessionNxtx } = useAvatarStore();

  return (
    <div
      className={`fixed z-[100] bottom-0 left-0 right-0 transition-transform duration-1000 ${
        isVisible ? "translate-y-0" : "translate-y-[100px]"
      }`}
    >
      {/* {!isDesktop && <DownloadAppBanner />} */}
      <nav className="flex justify-center z-10 bg-light_neutral10_to_dark_neutral50 md:hidden">
        {/* hide max-w-[720px] to make sure the background is full  */}
        <div className="relative w-full h-[64px] bg-white flex items-center justify-evenly px-8 gap-12 border-t border-neutral20_to_dark30">
          {menus && menus.length && !isLoading ? (
            ReduceArrayFromDuplicate(menus).map((menu: MenuProps) => {
              let colors =
                router.pathname == menu.href && !router.query.openGaia
                  ? iconColor.selected
                  : iconColor.default;

              let labels =
                router.pathname == menu.href && !router.query.openGaia
                  ? labelColor.selected
                  : labelColor.default;

              let icons =
                router.pathname == menu.href && !router.query.openGaia
                  ? `/images/${menu.id}-fill.png`
                  : `/images/${menu.id}-outline.png`;

              let bgColor = router.pathname == menu.href ? "#19093A" : "#fff";

              let textColor = router.pathname == menu.href ? "#fff" : "#19093A";

              return (
                <div
                  // href={menu.href as any}
                  key={"Nav-" + menu.id}
                  onClick={() => {
                    if (menu.id === "gaia") {
                      const currentQuery = { ...router.query };
                      if (currentQuery.openGaia === "true") {
                        delete currentQuery.openGaia;
                      } else {
                        currentQuery.openGaia = "true";
                      }
                      // @ts-ignore
                      router.push(
                        {
                          pathname: router.pathname,
                          query: currentQuery,
                        },
                        undefined,
                        { shallow: true }
                      );
                    } else {
                      //@ts-ignore
                      router.push(menu.href);
                      trackNavbar(menu.label);
                    }
                  }}
                >
                  <div className="p-5 w-fit">
                    {
                      {
                        "my-home": (
                          <div className="flex flex-col gap-2 items-center">
                            <IconHome icon={icons} />
                            <div className={labels}>{t(menu.label)}</div>
                          </div>
                        ),
                        "my-search": (
                          <div className="flex flex-col gap-2 items-center">
                            <IconSearch color={colors} classes="m-auto" />
                            <div className={labels}>{t(menu.label)}</div>
                          </div>
                        ),
                        "my-class": (
                          <div className="flex flex-col gap-2 items-center">
                            <IconMyClass icon={icons} />
                            <div className={labels}>{t(menu.label)}</div>
                          </div>
                        ),
                        gaia: (
                          <div className="relative w-[40px]">
                            <div className="absolute -bottom-[18px] flex flex-col gap-4 items-center">
                              <IconGaiaMenu />
                              <div>
                                {router.query.openGaia === "true" &&
                                !isLoadingSessionNxtx ? (
                                  <Image
                                    alt="nexa"
                                    src={"/nexa-text-icon.svg"}
                                    width={33}
                                    height={14}
                                  />
                                ) : (
                                  <Image
                                    alt="nexa"
                                    src={"/nexa-text-icon-neutral.svg"}
                                    width={33}
                                    height={14}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        ),
                        leaderboard: (
                          <div className="flex flex-col gap-2 items-center">
                            <IconLeaderboard icon={icons} />
                            <div className={labels}>{t(menu.label)}</div>
                          </div>
                        ),
                        dashboard: (
                          <div className="flex flex-col gap-2 items-center">
                            <IconDashboard icon={icons} />
                            <div className={labels}>{menu.label}</div>
                          </div>
                        ),
                        "my-account": (
                          <div className="flex flex-col gap-2 place-items-center">
                            {renderUser(bgColor, textColor)}
                            <div className={labels}>{t(menu.label)}</div>
                          </div>
                        ),
                      }[menu.id]
                    }
                  </div>
                </div>
              );
            })
          ) : isLoading ? (
            <SkeletonWrapper className="grid grid-cols-5 p-5 gap-8">
              {[1, 2, 3, 4, 5].map((el) => (
                <SkeletonRect key={el} className="rounded-4 h-48 w-80" />
              ))}
            </SkeletonWrapper>
          ) : null}
        </div>
      </nav>
      {isBlueBirdTenant && !isDesktop && (
        <>
          <div className="border-b border-b-neutral20_to_dark30"></div>
          <div className="bg-white flex justify-center items-center gap-8 py-4">
            <p className="text-b2 font-light text-neutral50_to_dark20">
              Powered by
            </p>
            <Image
              src={theme == "dark" ? "/logo-white-2048.png" : "/logo-2048.png"}
              alt="learnext"
              width={70}
              height={21}
            />
          </div>
        </>
      )}
    </div>
  );
};
